import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { api } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Check, Building2, Sparkles, Mail, User } from "lucide-react";

const ROLES = [
  { value: "agent", label: "Agent" },
  { value: "agency_admin", label: "Agency admin" },
  { value: "support", label: "Support" },
  { value: "admin", label: "Admin (super)" },
];

/**
 * Admin dialog for approving a Google-auth access request.
 * Admin explicitly picks a role and either an existing agency or a new one.
 * Never silently auto-creates from the requester's free-text.
 */
export default function ApproveAccessRequestDialog({ open, request, onOpenChange, onApproved }) {
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [role, setRole] = useState("agent");
  const [agencyMode, setAgencyMode] = useState("existing"); // "existing" | "new"
  const [agencyId, setAgencyId] = useState("");
  const [agencyName, setAgencyName] = useState("");

  const suggestedMatch = useMemo(() => {
    if (!request?.agency) return null;
    const needle = request.agency.trim().toLowerCase();
    return agencies.find((a) => (a.name || "").trim().toLowerCase() === needle) || null;
  }, [agencies, request?.agency]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setRole("agent");
    setAgencyName(request?.agency || "");
    setAgencyId("");
    api.get("/agencies")
      .then((r) => setAgencies(r.data || []))
      .catch(() => setAgencies([]))
      .finally(() => setLoading(false));
  }, [open, request?.id, request?.agency]);

  // Once agencies load, pre-select the exact match if one exists.
  useEffect(() => {
    if (!open || loading) return;
    if (suggestedMatch) {
      setAgencyMode("existing");
      setAgencyId(suggestedMatch.id);
    } else if (agencies.length > 0) {
      setAgencyMode("new");
    }
  }, [open, loading, suggestedMatch, agencies.length]);

  const submit = async () => {
    if (!request) return;
    if (agencyMode === "existing" && !agencyId) {
      toast.error("Pick an existing agency or switch to 'Create new'");
      return;
    }
    if (agencyMode === "new" && !agencyName.trim()) {
      toast.error("Enter a name for the new agency");
      return;
    }
    setSubmitting(true);
    try {
      const body = agencyMode === "existing"
        ? { role, agency_id: agencyId }
        : { role, agency_name: agencyName.trim() };
      const { data } = await api.post(`/admin/access-requests/${request.id}/approve`, body);
      toast.success(`${request.email} approved as ${role}`);
      onApproved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approve failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!request) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-sm" data-testid="approve-access-dialog">
        <DialogHeader className="space-y-2">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
            Approve access
          </div>
          <DialogTitle className="font-display text-2xl font-black text-[#0B132B] tracking-tight">
            Grant access to {request.name}
          </DialogTitle>
          <DialogDescription className="text-xs text-[#6C757D]">
            Pick a role and place this agent in an existing agency, or explicitly create a new one.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="rounded-sm border border-[#0B132B]/10 bg-[#F8F9FA] p-3 text-sm space-y-1.5">
            <div className="flex items-center gap-2 text-[#0B132B]">
              <User size={13} className="text-[#6C757D]" />
              <span className="font-semibold">{request.name}</span>
              {request.selling_agent && (
                <span className="ml-auto text-[10px] uppercase tracking-[0.18em] px-1.5 py-0.5 bg-emerald-100 text-emerald-800 font-bold rounded-sm">
                  Selling
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-[#6C757D] text-xs">
              <Mail size={12} /> {request.email}
            </div>
            <div className="flex items-center gap-2 text-[#6C757D] text-xs">
              <Building2 size={12} /> Requested agency: <span className="italic">{request.agency || "—"}</span>
            </div>
            {request.notes && (
              <div className="text-xs text-[#6C757D] pt-1 mt-1 border-t border-[#E9ECEF]">
                "{request.notes}"
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B]">
              Role
            </Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger
                className="mt-2 h-10 rounded-sm border-[#0B132B]/15"
                data-testid="approve-access-role-trigger"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => (
                  <SelectItem
                    key={r.value}
                    value={r.value}
                    data-testid={`approve-access-role-${r.value}`}
                  >
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B]">
              Agency
            </Label>
            <RadioGroup
              value={agencyMode}
              onValueChange={setAgencyMode}
              className="mt-2 space-y-2"
              data-testid="approve-access-agency-mode"
            >
              <label
                className="flex items-start gap-3 p-3 rounded-sm border border-[#0B132B]/15 cursor-pointer hover:bg-[#F8F9FA] transition-colors"
                data-testid="approve-access-agency-existing-row"
              >
                <RadioGroupItem value="existing" className="mt-1" data-testid="approve-access-agency-existing-radio" />
                <div className="flex-1 space-y-2">
                  <div className="text-sm font-semibold text-[#0B132B]">
                    Place in existing agency
                  </div>
                  <Select
                    value={agencyId}
                    onValueChange={(v) => { setAgencyId(v); setAgencyMode("existing"); }}
                    disabled={agencyMode !== "existing" || loading}
                  >
                    <SelectTrigger
                      className="h-9 rounded-sm border-[#0B132B]/15 text-sm"
                      data-testid="approve-access-agency-select-trigger"
                    >
                      <SelectValue placeholder={loading ? "Loading agencies…" : "Choose agency…"} />
                    </SelectTrigger>
                    <SelectContent>
                      {agencies.map((a) => (
                        <SelectItem
                          key={a.id}
                          value={a.id}
                          data-testid={`approve-access-agency-option-${a.id}`}
                        >
                          {a.name}
                          {suggestedMatch?.id === a.id && (
                            <span className="ml-2 text-[10px] text-[#D4AF37] font-bold">
                              MATCH
                            </span>
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {suggestedMatch && (
                    <div className="flex items-center gap-1 text-[11px] text-[#6C757D]">
                      <Sparkles size={11} className="text-[#D4AF37]" />
                      Matched requester's typed agency
                    </div>
                  )}
                </div>
              </label>

              <label
                className="flex items-start gap-3 p-3 rounded-sm border border-[#0B132B]/15 cursor-pointer hover:bg-[#F8F9FA] transition-colors"
                data-testid="approve-access-agency-new-row"
              >
                <RadioGroupItem value="new" className="mt-1" data-testid="approve-access-agency-new-radio" />
                <div className="flex-1 space-y-2">
                  <div className="text-sm font-semibold text-[#0B132B]">
                    Create new agency
                  </div>
                  <Input
                    value={agencyName}
                    onChange={(e) => { setAgencyName(e.target.value); setAgencyMode("new"); }}
                    placeholder="Agency name"
                    disabled={agencyMode !== "new"}
                    className="h-9 rounded-sm border-[#0B132B]/15 text-sm"
                    data-testid="approve-access-agency-new-input"
                  />
                </div>
              </label>
            </RadioGroup>
          </div>
        </div>

        <DialogFooter className="mt-2 gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-sm"
            data-testid="approve-access-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || loading}
            className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="approve-access-confirm"
          >
            <Check size={14} className="mr-1.5" />
            {submitting ? "Approving…" : "Approve access"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
